import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  effect,
  ElementRef,
  input,
  OnDestroy,
  output,
  signal,
  ViewChild,
} from '@angular/core';
import { PdfViewerComponent } from '../../../../../../components/shared/pdf-viewer/pdf-viewer.component';

import {
  QaiButtonComponent,
  QaiIconComponent,
  QaiLoadingComponent,
  QaiPageIndicatorComponent,
} from '@qai/ui';
import { TranslocoModule } from '@jsverse/transloco';
import { DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  AudioCallControlService,
  ISegmentProvider,
} from 'src/app/services/learner/audio-call-control.service';
import { IPdfProgress } from 'src/app/interfaces/ipdf-learn-state';
import { debounceTime, distinctUntilChanged, Subject } from 'rxjs';
import { CallStoreService } from '../../../library/call-store/call-store.service';

const MIN_TIME_ON_LAST_PAGE_MS = 2000;
@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    QaiButtonComponent,
    QaiIconComponent,
    QaiLoadingComponent,
    QaiPageIndicatorComponent,
    TranslocoModule,
    PdfViewerComponent,
  ],
  selector: 'app-call-pathway-pdf',
  standalone: true,
  styleUrls: ['./call-pathway-pdf.component.scss'],
  templateUrl: './call-pathway-pdf.component.html',
})
export class CallPathwayPdfComponent implements OnDestroy, ISegmentProvider {
  @ViewChild(PdfViewerComponent) pdfViewer!: PdfViewerComponent;
  @ViewChild('centerRef') centerRef!: ElementRef<HTMLElement>;
  @ViewChild('pageIndicatorRef', { read: ElementRef })
  pageIndicatorRef!: ElementRef<HTMLElement>;
  readonly pdfUrl = input.required<string>();
  readonly enrollmentUuid = input.required<string>();
  readonly initialPage = input<number>(1);
  readonly isFirstAttempt = input<boolean>(true);
  readonly currentPage = signal(1);
  readonly totalPages = signal(0);
  readonly isLoaded = signal(false);
  readonly isZoomedBeyondPageWidth = signal(false);
  readonly isScrollable = signal(false);
  readonly isAtBottom = signal(false);
  readonly endCallTriggered = signal(false);
  private readonly scrolledPages = new Set<number>();
  private isNavigatingBackward = false;
  private readonly destroyRef = inject(DestroyRef);
  private readonly audioCallControlService = inject(AudioCallControlService);
  private readonly callStore = inject(CallStoreService);
  private audioCallStarted = false;
  private lastPageTimeout: any = null;
  readonly hasPageRendered = signal(false);
  readonly progressChange = output<IPdfProgress>();
  readonly pdfCompleted = output<void>();
  private isChangingPage = false;
  private pageChangeSnapshot = '';
  readonly callLearnUuid = input<string>('');
  private readonly segments: { page: number; text: string }[] = [];
  private readonly progressSubject = new Subject<{ page: number; total_pages: number }>();

  constructor(
    private readonly cdr: ChangeDetectorRef,
  ) {
    this.progressSubject
      .pipe(
        debounceTime(500),
        distinctUntilChanged((a, b) => a.page === b.page),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(({ page, total_pages }) => {
        this.emitProgress(page, total_pages);
      });

    effect(() => {
      const src = this.pdfUrl();
      this.isLoaded.set(false);
      this.endCallTriggered.set(false);
      this.hasPageRendered.set(false);
      this.isScrollable.set(false);
      this.isAtBottom.set(false);
      this.audioCallStarted = false;
      this.scrolledPages.clear();
      this.isNavigatingBackward = false;
      this.segments.length = 0;
      this.pageChangeSnapshot = '';
      if (!src) {
        this.currentPage.set(1);
        return;
      }
    });

    // Detect last page and trigger API
    effect(() => {
      const page = this.currentPage();
      const total = this.totalPages();
      const loaded = this.isLoaded();

      if (!loaded || total === 0 || this.endCallTriggered()) {
        this.clearLastPageTimer();
        return;
      }

      if (page === total) {
        this.startLastPageTimer();
      } else {
        this.clearLastPageTimer();
      }
    });

    effect(() => {
      const finalTranscript = this.callStore.getSpeechTranscript();
      const partialTranscript = this.callStore.getSpeechTranscriptPartial();
      const isLoaded = this.isLoaded();
    
      if (!isLoaded || this.isChangingPage) {
        return;
      }
    
      const baseTranscript = this.pageChangeSnapshot
        ? partialTranscript
        : finalTranscript;
    
      if (!baseTranscript?.trim()) {
        return;
      }
    
      const newText = this.getNewPortionSincePageChange(
        baseTranscript.trim()
      );
    
      if (newText) {
        this.updateCurrentSegment(newText);
      }
    });
  }

  private getNewPortionSincePageChange(transcript: string): string {
    if (!this.pageChangeSnapshot) {
      return transcript;
    }

    if(!transcript) {
      return '';
    }
  
    const committedText = this.pageChangeSnapshot;
  
    if (!committedText) {
      return transcript;
    }
  
    const lowerTranscript = transcript.toLowerCase();
    const lowerCommitted = committedText.toLowerCase();
  
    if (lowerTranscript.startsWith(lowerCommitted)) {
      return transcript.slice(committedText.length).trim();
    }
  
    return transcript;
  }

  private updateCurrentSegment(text: string): void {
    const existingIndex = this.segments.findIndex(s => s.page === this.currentPage());
    if (existingIndex >= 0) {
      this.segments[existingIndex].text = text;
    } else {
      this.segments.push({ page: this.currentPage(), text });
    }
    // Remove any segments with empty text
    const emptyIndex = this.segments.findIndex(s => !s.text.trim());
    if (emptyIndex >= 0) this.segments.splice(emptyIndex, 1);
  }

  getUserSpokenResponse(): string {
    const transcriptFallback =
      (this.callStore.getSpeechTranscriptPartial() ?? '').trim() ||
      (this.callStore.getSpeechTranscript() ?? '').trim();

    if (this.segments.length === 0) {
      return transcriptFallback;
    }
    const userSpokenResponse = this.segments
      .map((s) => `${s.page} | ${s.text}`)
      .join('\n');
    // Clear segments for next utterance
    this.segments.length = 0;
    this.pageChangeSnapshot = '';
    return userSpokenResponse;
  }

  getCallLearnUuid(): string {
    return this.callLearnUuid() ?? '';
  }

  onPagesLoaded(total: number): void {
    this.totalPages.set(total);
    this.isLoaded.set(true);
    this.startAudioCallOnce();
    this.audioCallControlService.registerSegmentProvider(this);
    this.cdr.detectChanges();
    this.emitProgress(this.currentPage(), total);
    const startPage = this.initialPage();
    if (startPage > 1 && startPage <= total) {
      requestAnimationFrame(() => {
        if (this.pdfViewer) {
          this.pdfViewer.goToPage(startPage);
        }
      });
    }
  }

  private startAudioCallOnce(): void {
    if (this.audioCallStarted) return;
    this.audioCallStarted = true;
    this.callStore.setIsAudioCallMode(true);
    this.audioCallControlService.startCall().catch((err) => {
      console.error('Audio call start failed', err);
      this.audioCallStarted = false;
    });
  }

  onPageChange(page: number): void {
    if (this.isNavigatingBackward) {
      this.scrolledPages.add(page);
      this.isNavigatingBackward = false;
    }
    this.isAtBottom.set(false);
    this.isChangingPage = true;
    this.commitSpeechToCurrentPage();
    this.currentPage.set(page);
    this.isChangingPage = false;
    this.progressSubject.next({ page, total_pages: this.totalPages() });
  }

  private commitSpeechToCurrentPage(): void {
    const partial = this.callStore.getSpeechTranscriptPartial() ?? '';
    if(!partial){
      this.segments.length = 0;
      this.pageChangeSnapshot = '';
      return;
    }
    const newPortion = this.getNewPortionSincePageChange(partial);
    if (newPortion) {
      this.updateCurrentSegment(newPortion);
      this.pageChangeSnapshot = partial;
    }
  }

  onPageRendered(): void {
    if (!this.isLoaded()) return;
    this.hasPageRendered.set(true);
    if (!this.isScrollable()) {
      requestAnimationFrame(() => this.positionPageIndicator());
    }
  }

  onBeyondPageWidth(beyond: boolean): void {
    this.isZoomedBeyondPageWidth.set(beyond);
    this.cdr.markForCheck();
    if (!beyond && !this.isScrollable()) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => this.positionPageIndicator());
      });
    }
  }

  onIsScrollable(scrollable: boolean): void {
    this.isScrollable.set(scrollable);
    if (scrollable) {
      this.returnIndicatorToContainer();
    } else if (this.hasPageRendered()) {
      requestAnimationFrame(() => this.positionPageIndicator());
    }
  }

  onScrolledToBottom(atBottom: boolean): void {
    this.isAtBottom.set(atBottom);
    if (atBottom) {
      this.scrolledPages.add(this.currentPage());
    }
  }

  private positionPageIndicator(): void {
    const center = this.centerRef?.nativeElement;
    const indicator = this.pageIndicatorRef?.nativeElement;
    const pageEl = center?.querySelector('.page') as HTMLElement | null;
    if (!center || !indicator || !pageEl) return;
    if (!pageEl.contains(indicator)) {
      pageEl.appendChild(indicator);
    }
  }

  private returnIndicatorToContainer(): void {
    const indicator = this.pageIndicatorRef?.nativeElement;
    const container = this.centerRef?.nativeElement;
    if (!indicator || !container) return;
    if (!container.contains(indicator)) {
      container.appendChild(indicator);
    }
  }

  previousPage(): void {
    if (this.canGoPrevious) {
      this.isNavigatingBackward = true;
      this.pdfViewer.goToPreviousPage();
    }
  }

  nextPage(): void {
    if (this.canGoNext) {
      this.pdfViewer.goToNextPage();
    }
  }

  get canGoPrevious(): boolean {
    return this.currentPage() > 1;
  }

  get isScrollGateLocked(): boolean {
    return this.isFirstAttempt() && this.isScrollable() && !this.scrolledPages.has(this.currentPage());
  }

  get showScrollHint(): boolean {
    return this.isScrollGateLocked;
  }

  get canGoNext(): boolean {
    return this.currentPage() < this.totalPages() && !this.isScrollGateLocked;
  }

  private triggerEndCall(): void {
    this.handlePdfCompleted();
  }

  private startLastPageTimer(): void {
    if (this.lastPageTimeout || this.endCallTriggered()) return;

    this.lastPageTimeout = setTimeout(() => {
      this.endCallTriggered.set(true);
      this.triggerEndCall();
    }, MIN_TIME_ON_LAST_PAGE_MS);
  }

  private clearLastPageTimer(): void {
    if (this.lastPageTimeout) {
      clearTimeout(this.lastPageTimeout);
      this.lastPageTimeout = null;
    }
  }

  ngOnDestroy(): void {
    this.progressSubject.complete();
    this.clearLastPageTimer();
    this.callStore.setIsAudioCallMode(false);
    this.audioCallControlService.unregisterSegmentProvider();
    this.audioCallControlService.endCall().catch((err) => {
      console.error('Audio call end failed', err);
    });
  }

  private emitProgress(page: number, total_pages: number): void {
    console.log("emitProgress: page: ", page, "total_pages: ", total_pages);
    this.progressChange.emit({ page, total_pages });
  }

  protected handlePdfCompleted(): void {
    this.pdfCompleted.emit();
  }
}